import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';

import RelatedWorkOrders from 'af-root/scenes/Company/Resources/Shared/RelatedWorkOrders';
import ResourcePreview from 'af-root/scenes/Company/Resources/Shared/ResourcePreview';

import type { EquipmentViewModel } from 'ab-viewModels/equipment/equipmentPreview.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import { AVAILABLE_EQUIPMENT_STATUS } from 'ab-common/constants/equipment';
import { DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';
import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { SegmentLabel } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ImageTag from 'af-components/Image';
import LabelWithPills from 'af-components/LockedPillsValue';
import LockedValue from 'af-components/LockedValue';
import StatusLabel from 'af-components/StatusLabel';

import * as EquipmentActions from 'af-actions/equipment';

import Loading from './Loading';
import SmallToolTable from './SmallToolTable';
import styles from './styles.module.scss';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const Preview: React.FC<Props> = (props) => {
	const { location: { state: { orgAlias } }, companyName, findById, match: { params: { id } }, findRelatedWorkOrdersTable } = props;

	const [equipment, setEquipment] = React.useState<Nullable<EquipmentViewModel>>(null);

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			const _equipment = await findById(id);
			setEquipment(_equipment);
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const breadcrumbs = React.useMemo(() => {
		return [
			equipment?.isDeleted ?
				{
					label: 'Deleted Equipment',
					url: `${CLIENT.COMPANY.EQUIPMENT.DELETED_TABLE(orgAlias, companyName)}`,
				}
				: {
					label: 'Equipment',
					url: `${CLIENT.COMPANY.EQUIPMENT.TABLE(orgAlias, companyName)}`,
				},
			{ label: 'Preview' },
		];
	}, [companyName, equipment?.isDeleted, orgAlias]);

	const fetchWorkOrders = React.useCallback(async (tableRequestModel: TableQuery, startDate: Date, endDate: Date) => {
		return await findRelatedWorkOrdersTable(+id, tableRequestModel, startDate, endDate);
	}, [findRelatedWorkOrdersTable, id]);

	const code = React.useMemo(() => {
		if (equipment) {
			const companyAppendix = equipment.showCompanyAbbreviation ? `-${equipment.companyAbbreviation}` : '';
			return `${equipment.code}${companyAppendix}`;
		}
		return '';
	}, [equipment]);
	return (
		<>
			{equipment ?
				<>
					<ResourcePreview
						backToListLabel="Back to Equipment List"
						backToListUrl={`${CLIENT.COMPANY.EQUIPMENT.TABLE(orgAlias, companyName)}`}
						breadcrumbs={breadcrumbs}
						editUrl={CLIENT.COMPANY.EQUIPMENT.EDIT(equipment.id?.toString?.(), orgAlias, companyName)}
						isDeleted={equipment.isDeleted}
					>
						<Row className="row row--flex">
							<div className="avatar__container avatar__container--double">
								<div className="avatar">
									<ImageTag
										fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
										minSize={BlobStorageImageSizeContainer.SIZE_200X200}
										src={equipment.frontImageUrl as string}
										tryOriginal={true}
										tryRoot={true}
									/>
								</div>
								<div className="avatar">
									<ImageTag
										fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
										minSize={BlobStorageImageSizeContainer.SIZE_200X200}
										src={equipment.backImageUrl as string}
										tryOriginal={true}
										tryRoot={true}
									/>
								</div>
							</div>
							<div className="avatar__neighbour">
								<Row className="row--padded">
									<Col md={8}><LockedValue defaultValue="N/A" label="Equipment ID" value={code} /></Col>
									<Col md={8}><LockedValue defaultValue="N/A" label="Equipment Specification" value={equipment.specification} /></Col>
									<Col md={8}>
										<LockedValue
											defaultValue="N/A"
											label="Current Status"
											value={
												<StatusLabel
													isAvailable={!!equipment?.isStatusAvailable}
													label={equipment.equipmentStatusName ?? AVAILABLE_EQUIPMENT_STATUS}
												/>
											}
										/>
									</Col>
								</Row>
								<Row className="row--padded-bottom">
									<Col md={8}><LockedValue defaultValue="N/A" label="Equipment Cost" value={equipment.equipmentCost?.subcategory} /></Col>
									<Col md={8}><LockedValue defaultValue="N/A" label="Location" value={equipment?.location?.nickname} /></Col>
									<Col md={8} />
								</Row>
							</div>
						</Row>
						<Row>
							<Col className="m-t-m m-b-m" sm={24}>
								<SegmentLabel
									label="Contacts"
									tooltip="People responsible for this equipment, for internal reference if questions arise."
								/>
							</Col>
						</Row>
						<Row>
							<Col md={6}><LockedValue defaultValue="N/A" label="First Contact" value={equipment.primaryContact} /></Col>
							<Col md={6}><LockedValue defaultValue="N/A" label="Second Contact" value={equipment.secondaryContact} /></Col>
							<Col md={12} />
						</Row>
						<Row>
							<Col className="m-t-m m-b-m" sm={24}>
								<SegmentLabel
									label="Skills and Licenses"
									tooltip="Required skills, licenses, and vehicle/registration details for this equipment. Skills may be auto-populated from the associated Equipment Cost."
								/>
							</Col>
						</Row>
						<Row>
							<Col md={12}>
								<LabelWithPills
									colorKey="color"
									items={equipment.skills}
									label="Skills"
									labelKey="name"
								/>
							</Col>
						</Row>
						<Row className="row--padded">
							<Col md={24}>
								<LabelWithPills
									colorKey="color"
									items={equipment.equipmentCost?.skills ?? []}
									label="Skills Defined by Equipment Cost"
									labelKey="name"
								/>
							</Col>
						</Row>
						<Row>
							<Col md={24}><LockedValue defaultValue="N/A" label="Licenses" value={equipment.licenses} /></Col>
						</Row>

						<Row className="row--padded">
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Plate Number" value={equipment.plateNumber} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Plate Type" value={equipment.plateType} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Registration State" value={equipment.registrationState} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue
									defaultValue="N/A"
									label="Registration Expiration"
									value={equipment.registrationExpiration
										? TimeUtils.formatDate(equipment.registrationExpiration, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY)
										: undefined}
								/>
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue
									defaultValue="N/A"
									label="Inspection Expiration"
									value={equipment.inspectionStickerExpiration
										? TimeUtils.formatDate(equipment.inspectionStickerExpiration, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY)
										: undefined}
								/>
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="VIN Number" value={equipment.vinNumber} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="GVW" value={equipment.gvw?.toString()} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Year" value={equipment.year?.toString()} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Make" value={equipment.make} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Model" value={equipment.model} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Drivers Alert ID" value={equipment.driversAlertId} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="Toll Pass ID" value={equipment.tollPassId} />
							</Col>
							<Col className={styles['info-field']} md={6}>
								<LockedValue defaultValue="N/A" label="HUT Number" value={equipment.hutNumber} />
							</Col>
						</Row>

					</ResourcePreview>
					<SmallToolTable
						companyName={companyName}
						orgAlias={orgAlias}
						toolTrackingRecords={equipment.toolTrackingRecords}
					/>
				</>
				: <Loading />
			}
			<div className="form-segment">
				<Breadcrumbs items={[{ label: 'Related Work Orders' }]} />
				<RelatedWorkOrders
					companyName={companyName}
					csvName={`${companyName}_${equipment?.code}_work_orders.csv`}
					fetchWorkOrders={fetchWorkOrders}
					tableName={TableNameEnum.EQUIPMENT_WORK_ORDERS}
				/>
			</div>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findById: EquipmentActions.findById,
		findRelatedWorkOrdersTable: EquipmentActions.findRelatedWorkOrdersTable,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(Preview);
